import { get, isArray, sortBy } from 'lodash';
import moment from 'moment';
import {
  defaultDateTimeFormat,
  managerPermissionDescription,
  lettersRegex,
  numberRegex,
  overageTypes,
  licenseTypes,
} from 'shared/constants';
import store from 'redux/store';

export const slugify = (t, separator) => {
  let text = t.toString().toLowerCase().trim();

  const sets = [
    { to: 'a', from: '[ÀÁÂÃÄÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶ]' },
    { to: 'c', from: '[ÇĆĈČ]' },
    { to: 'd', from: '[ÐĎĐÞ]' },
    { to: 'e', from: '[ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ]' },
    { to: 'g', from: '[ĜĞĢǴ]' },
    { to: 'h', from: '[ĤḦ]' },
    { to: 'i', from: '[ÌÍÎÏĨĪĮİỈỊ]' },
    { to: 'j', from: '[Ĵ]' },
    { to: 'ij', from: '[Ĳ]' },
    { to: 'k', from: '[Ķ]' },
    { to: 'l', from: '[ĹĻĽŁ]' },
    { to: 'm', from: '[Ḿ]' },
    { to: 'n', from: '[ÑŃŅŇ]' },
    { to: 'o', from: '[ÒÓÔÕÖØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ]' },
    { to: 'oe', from: '[Œ]' },
    { to: 'p', from: '[ṕ]' },
    { to: 'r', from: '[ŔŖŘ]' },
    { to: 's', from: '[ßŚŜŞŠ]' },
    { to: 't', from: '[ŢŤ]' },
    { to: 'u', from: '[ÙÚÛÜŨŪŬŮŰŲỤỦỨỪỬỮỰƯ]' },
    { to: 'w', from: '[ẂŴẀẄ]' },
    { to: 'x', from: '[ẍ]' },
    { to: 'y', from: '[ÝŶŸỲỴỶỸ]' },
    { to: 'z', from: '[ŹŻŽ]' },
    { to: '-', from: "[·/_,:;']" },
  ];

  sets.forEach((set) => {
    text = text.replace(new RegExp(set.from, 'gi'), set.to);
  });

  text = text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/&/g, '-and-') // Replace & with 'and'
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text

  if (typeof separator !== 'undefined' && separator !== '-') {
    text = text.replace(/-/g, separator);
  }

  return text;
};

export const isValidDate = (dateString) => {
  const regEx = /^\d{4}-\d{2}-\d{2}$/;

  if (typeof dateString !== 'string') {
    return false;
  }

  if (!dateString.match(regEx)) {
    return false;
  }
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) {
    return false;
  }
  return d.toISOString().slice(0, 10) === dateString;
};

export const formatDate = (
  val,
  formatType = defaultDateTimeFormat,
  fallback = '-',
) => {
  const isValid = moment(val).isValid();

  if (!val || !isValid) {
    return fallback;
  }
  return moment(val).format(formatType);
};

export const displayValue = (val, fallback = '-') => {
  if (!val && val !== 0) {
    return fallback;
  }
  return val;
};

export const getSectionListData = (sectionData) => {
  if (Array.isArray(sectionData)) {
    return sectionData;
  }

  const results = get(sectionData, 'results');

  if (Array.isArray(results)) {
    return results;
  }

  return [];
};

export const getLicenseStatus = (licenseData, t) => {
  const data = get(licenseData, 'original', licenseData);
  const enabled = get(data, 'enabled');
  const active = get(data, 'active');
  const lastActivationDevice = get(data, 'last_activation_device');

  if (!enabled) {
    return { status: 'error', message: t('Disabled') };
  }

  if (!lastActivationDevice) {
    return { status: 'inactive', message: t('Not Activated') };
  }

  return {
    status: active ? 'success' : 'error',
    message: active ? t('Active') : t('Inactive'),
  };
};

export const displayMaxOveragesValue = (data) => {
  const allowOverages = get(data, 'allow_overages');
  const maxOverages = get(data, 'max_overages');

  if (!allowOverages) {
    return '-';
  }

  if (maxOverages === 0) {
    return 'Unlimited';
  }

  return displayValue(maxOverages);
};

export const capitalizeFirstLetter = (string) => {
  if (!string) {
    return undefined;
  }
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const transformSort = (sortObject = []) => {
  const formattedSortObject = sortObject.map((sortObj) => {
    if (sortObj.id === 'license_key') {
      return {
        id: 'customLicenseSort',
        desc: sortObj.desc,
      };
    }
    if (sortObj.id === 'order_store_id') {
      return {
        id: 'order.store_id',
        desc: sortObj.desc,
      };
    }
    return sortObj;
  });

  const fieldsToTransform = [
    'customer.email',
    'customer.company_name',
    'customer.last_name',
    'customer.reference',
    'product.product_name',
    'order.store_id',
  ];
  const rewriteMap = {
    product: 'product.product_name',
  };
  return formattedSortObject.map((s) => {
    const field = rewriteMap[s.id] || s.id;
    if (fieldsToTransform.indexOf(field) >= 0) {
      return {
        id: field.split('.').join('__'),
        desc: s.desc,
      };
    }
    return s;
  });
};

export const isUserManager = (user) => {
  if (!user) {
    return false;
  }
  const isManager = get(user, 'is_manager');
  // const isIndependentManager = get(user, 'is_independent_manager');
  // return isManager || isIndependentManager;
  return isManager;
};

export const isJSON = (val) => {
  try {
    JSON.parse(val);
  } catch (e) {
    return false;
  }
  return true;
};

export const parseObject = (val, fallback = undefined) => {
  let data = {};

  if (!val || !isJSON(val)) {
    return fallback || data;
  }

  const result = JSON.parse(val);
  if (typeof result === 'object' && !Array.isArray(result)) {
    data = result;
  }

  return data;
};

export const copyText = (val) => {
  if (!val) {
    return false;
  }
  const textField = document.createElement('textarea');
  textField.innerText = val;
  document.body.appendChild(textField);
  textField.select();
  const result = document.execCommand('copy');
  textField.remove();
  return result;
};

export const getTableCustomerName = (data, fallback = '-') => {
  const firstName = get(data, 'customer.first_name', '');
  const lastName = get(data, 'customer.last_name', '');

  if (!firstName && !lastName) {
    return fallback;
  }

  if (!!firstName && !lastName) {
    return `${firstName}`;
  }

  if (!firstName && !!lastName) {
    return `${lastName}`;
  }

  const name = `${lastName}, ${firstName}`;
  return name.trim();
};

export const getPortalData = (
  portalData,
  property,
  fallbackData,
  fallback = false,
) => {
  // eslint-disable-next-line no-prototype-builtins
  const doesPropExist =
    typeof portalData === 'object' && portalData.hasOwnProperty(property);

  if (doesPropExist) {
    return portalData[property];
  }

  return fallbackData[property] || fallback;
};

export const getCurrentUserPermissions = (state) => {
  const normalizePermissions = (permissions) => {
    if (Array.isArray(permissions)) {
      return permissions;
    }

    const permissionResults = get(permissions, 'results');

    if (Array.isArray(permissionResults)) {
      return permissionResults;
    }

    return null;
  };

  const userPermissions = normalizePermissions(
    get(state, 'user.details.permissions'),
  );

  if (userPermissions) {
    return userPermissions;
  }

  const userID = get(state, 'user.details.id');
  const sectionPermissions = normalizePermissions(
    userID
      ? get(state, `user.userSections.${userID}.managerPermissions.data`)
      : null,
  );

  if (sectionPermissions) {
    return sectionPermissions;
  }

  const managerPermissions = normalizePermissions(
    get(state, 'user.managerPermissions'),
  );

  if (managerPermissions) {
    return managerPermissions;
  }

  return [];
};

export const getUserPermissions = () => {
  const state = store.getState();
  return getCurrentUserPermissions(state);
};

const isNonEmptyObject = (value) =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0;

export const getCurrentUserCompany = (state) => {
  const currentCompany = get(state, 'user.company');

  if (isNonEmptyObject(currentCompany)) {
    return currentCompany;
  }

  const detailsCompany = get(state, 'user.details.company');

  if (isNonEmptyObject(detailsCompany)) {
    return detailsCompany;
  }

  const userID = get(state, 'user.details.id');
  const sectionCompany = userID
    ? get(state, `user.userSections.${userID}.company.data`)
    : null;

  if (isNonEmptyObject(sectionCompany)) {
    return sectionCompany;
  }

  return {};
};

export const hasPermission = (permissionsListOrCode, item) => {
  let permissionsList = permissionsListOrCode;
  let permissionCode = item;

  if (!Array.isArray(permissionsListOrCode)) {
    permissionsList = getUserPermissions();
    permissionCode = typeof item === 'undefined' ? permissionsListOrCode : item;
  }

  if (!Array.isArray(permissionsList)) {
    return false;
  }

  const isPermissionEnabled = permissionsList.find(
    (p) => p.code === permissionCode,
  );
  return !!isPermissionEnabled;
};

export const formatManagerPermissions = (list) => {
  const data = list.map((i) => {
    const label = i.code.split('_').join(' ');
    const desc = managerPermissionDescription[i.code];
    return {
      ...i,
      value: i.code,
      label: capitalizeFirstLetter(label),
      desc,
    };
  });
  return data;
};

export const mapProductsToSelector = (products, value = 'product_name') => {
  const productsOptions = [];
  if (!products) {
    return productsOptions;
  }
  if (Array.isArray(products)) {
    products.forEach((product) => {
      const option = {
        label: get(product, 'product_name'),
        value: get(product, `${value}`),
        data: product,
      };
      productsOptions.push(option);
    });
  }
  return productsOptions;
};

export const mapBundlesToSelector = (bundles) => {
  const productsOptions = [];
  if (!bundles) {
    return productsOptions;
  }
  if (Array.isArray(bundles)) {
    bundles.forEach((bundle) => {
      const option = {
        label: get(bundle, 'product.product_name'),
        value: get(bundle, 'product.id'),
        data: bundle,
      };
      productsOptions.push(option);
    });
  }
  return productsOptions;
};

export const getDefaultLicensePolicy = (products, policies, productToEdit) => {
  let product;
  if (productToEdit) {
    product = productToEdit;
  } else {
    product = get(products, '[0]');
  }
  if (get(product, 'selected_license_policy')) {
    return get(product, 'selected_license_policy');
  }
  const productPolicies = policies.filter(
    (p) => get(p, 'product.id') === get(product, 'id'),
  );
  const defaultPolicy = productPolicies.find((p) => p.is_default);
  return defaultPolicy || productPolicies[0];
};

export const formatPolicies = (policies) =>
  policies.map((p) => ({
    ...p,
    label: get(p, 'name'),
    value: get(p, 'code'),
  }));

export const formatPolicyCFields = (cfields) =>
  cfields.map((pf) => ({
    ...pf,
    label: get(pf, 'product_custom_field.name'),
    value: get(pf, 'product_custom_field.id'),
    cf_value: get(pf, 'default_value'),
  }));

export const mapCustomFieldsToSelector = (product) => {
  const fieldsOptions = [];
  const customFields = get(product, 'custom_fields', []);

  if (Array.isArray(customFields)) {
    customFields.forEach((cf) => {
      const option = {
        label: get(cf, 'name'),
        value: get(cf, 'id'),
        cf_value: get(cf, 'default_value'),
        data: cf,
      };
      fieldsOptions.push(option);
    });
  }
  return fieldsOptions;
};

export const getCharsFromString = (value, fallback = '') => {
  if (!value || typeof value !== 'string') {
    return fallback;
  }
  const charsFromString = value.match(lettersRegex);

  if (!charsFromString || charsFromString.length === 0) {
    return fallback;
  }

  return charsFromString.join();
};

export const getNumbersFromString = (value, fallback = '') => {
  if (!value || typeof value !== 'string') {
    return fallback;
  }
  const numbersFromString = value.match(numberRegex);

  if (!numbersFromString || numbersFromString.length === 0) {
    return fallback;
  }

  return numbersFromString.join();
};

export const formatOrderBy = (orderBy) => {
  if (!orderBy || !Array.isArray(orderBy) || !orderBy.length) {
    return '';
  }

  const orderingString = orderBy
    .map((orderType) => `${orderType.desc ? '-' : ''}${orderType.id}`)
    .join(',');

  return `&order_by=${orderingString}`;
};

export const getTotalLicenseIssued = (counter = []) => {
  const count = counter.reduce(
    (total, item) => total + get(item, 'created_licenses_count', 0),
    0,
  );
  return count;
};

export const getRemainingLicenses = (productID, counter = []) => {
  if (!productID) {
    return 0;
  }
  const product = counter.find((i) => get(i, 'product.id') === productID);
  if (!product) {
    return 0;
  }
  const used = get(product, 'created_licenses_count', 0);
  const limit = get(product, 'num_of_licenses');
  if (limit === null) {
    return null;
  }
  const remaining = limit - used;
  return remaining;
};

export const getAppConfigVar = (varName, fallback) => {
  const { appConfig } = window;

  if (!appConfig) {
    return fallback;
  }

  const variable = appConfig[varName];

  if (!variable) {
    return fallback;
  }

  return variable;
};

export const isFeatureEnabled = (feature, features) => {
  const state = store.getState();
  const featureList =
    features || get(state, 'companyFeatures.features.results', []);
  const actualFeature = isArray(featureList)
    ? featureList.find((f) => f.key === feature)
    : undefined;

  // Check if feature exists
  if (!get(actualFeature, 'is_active')) {
    return false;
  }

  // Feature is enabled
  return true;
};

export const isFeatureAvailable = (feature, value = 0, features) => {
  const state = store.getState();
  const featureList =
    features || get(state, 'companyFeatures.features.results', []);
  const actualFeature = featureList.find((f) => f.key === feature);

  // Check if feature exists
  if (!actualFeature) {
    return false;
  }

  if (actualFeature.value === undefined) {
    return false;
  }

  // Check if feature has infinite usage
  if (actualFeature.value === -1) {
    return true;
  }

  // Check if usage has depleted usage
  if (typeof actualFeature.value === 'number' && value >= actualFeature.value) {
    return false;
  }

  return true;
};

export const mapFeaturesToSelector = (features) => {
  const featureOptions = [];

  if (Array.isArray(features)) {
    features.forEach((feature) => {
      const option = {
        label: get(feature, 'product_feature.name', get(feature, 'name')),
        value: get(feature, 'product_feature.id', get(feature, 'id')),
        code: get(feature, 'product_feature.code', get(feature, 'code')),
        name: get(feature, 'product_feature.name', get(feature, 'name')),
        feature_type: get(feature, 'feature_type'),
        expiry_date: get(feature, 'expiry_date', null),
        max_consumption: get(feature, 'max_consumption'),
        allow_unlimited_consumptions: get(
          feature,
          'allow_unlimited_consumptions',
        ),
        allow_negative_consumptions: get(
          feature,
          'allow_negative_consumptions',
        ),
        allow_overages: get(feature, 'allow_overages'),
        max_overages: get(feature, 'max_overages'),
        reset_consumption: get(feature, 'reset_consumption'),
        consumption_period: get(feature, 'consumption_period'),
        is_floating: get(feature, 'is_floating'),
        is_floating_cloud: get(feature, 'is_floating_cloud'),
        floating_timeout: get(feature, 'floating_timeout'),
        floating_users: get(feature, 'floating_users'),
        metadata: get(feature, 'metadata', ''),
        valid_duration: get(feature, 'valid_duration', null),
        data: feature,
        can_borrow: get(feature, 'can_borrow', false),
        max_borrow_time: get(feature, 'max_borrow_time', 0),
      };
      featureOptions.push(option);
    });
  }
  return sortBy(featureOptions, 'label');
};

export const getPolicyHasMaxTransfers = (policy) => {
  const maxTransfers = get(policy, 'max_transfers');
  if (!maxTransfers || maxTransfers === 0) {
    return false;
  } // no limit
  if (Number(maxTransfers) < 0) {
    return false;
  } // prevent device transfer
  if (Number(maxTransfers) > 0) {
    return true;
  }
  return false;
};

export const preparePolicyFeatures = (policyFeatures) => {
  return policyFeatures.map((feature) => {
    return {
      ...feature,
      name: get(feature, 'product_feature.name', get(feature, 'name')),
      code: get(feature, 'product_feature.code', get(feature, 'code')),
    };
  });
};

export const isJSONObject = (val) => {
  try {
    const obj = JSON.parse(val);
    return typeof obj === 'object';
  } catch (e) {
    return false;
  }
};

export const parseMetadata = (val, fallback = '') => {
  if (!val) {
    return fallback;
  }
  if (typeof val === 'object') {
    return JSON.stringify(val);
  }

  if (!val || !isJSONObject(val)) {
    return fallback;
  }

  if (!Object.keys(val).length) {
    return fallback;
  }

  return val;
};

export const setDefaultOverageType = (product) => {
  const maxOverage = get(product, 'max_overages');

  if (maxOverage === 0) {
    return overageTypes.unlimited;
  }

  return overageTypes.custom;
};

export const isConsumptionLicense = (license) => {
  return (
    get(license, 'license_type') === licenseTypes.consumption ||
    get(license, 'default_license_type') === licenseTypes.consumption ||
    get(license, 'product.default_license_type') === licenseTypes.consumption
  );
};

export const hasResetValidityPeriod = (license) => {
  return (
    Boolean(get(license, 'validity_period')) ||
    get(license, 'license_type') === licenseTypes.time_limited ||
    get(license, 'license_type') === licenseTypes.subscription ||
    get(license, 'default_license_type') === licenseTypes.time_limited ||
    get(license, 'default_license_type') === licenseTypes.subscription ||
    get(license, 'product.default_license_type') ===
      licenseTypes.time_limited ||
    get(license, 'product.default_license_type') === licenseTypes.subscription
  );
};

export const hasResetMaintenancePeriod = (license) => {
  return (
    get(license, 'enable_maintenance_period', false) ||
    get(license, 'product.enable_maintenance_period', false)
  );
};
